import { useCallback, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { useAppSelector } from "store/hooks"
import { deviceSystemAgeOptions } from "constants/DeviceSystemAgeOptions"

const useUrlQueryParams = () => {
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const userProfile = useAppSelector(state => state.user.userProfile)

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(search)
    const result: Record<string, string> = {}
    params.forEach((value, key) => {
      if (key === "custom_param") {
        return
      }
      if (key === "system_age" && isNaN(Number(value))) {
        const systemAgeOption = deviceSystemAgeOptions.find(option => option.value === value)
        if (systemAgeOption) {
          if (systemAgeOption.start !== undefined) {
            result["system_age_start"] = systemAgeOption.start
          }
          if (systemAgeOption.end !== undefined) {
            result["system_age_end"] = systemAgeOption.end
          }
        }
      } else {
        result[key] = value
      }
    })

    if (
      userProfile &&
      !params.has("perPage") &&
      !params.has("top15") &&
      !params.has("bottom15") &&
      (userProfile.type === 1 || userProfile.type === 2)
    ) {
      result["perPage"] = "50"
    }

    return result
  }, [search])

  const originalQueryParams = useMemo(() => {
    const params = new URLSearchParams(search)
    const result: Record<string, string> = {}
    params.forEach((value, key) => {
      result[key] = value
    })
    return result
  }, [search])

  const setUrlQueryParams = useCallback(
    (updates: Record<string, string | number | true | null>, clearParams?: boolean) => {
      const params = new URLSearchParams(clearParams ? "" : search)

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key)
        } else {
          params.set(key, String(value))
        }
      })
      navigate(`${pathname}?${params.toString()}`, { replace: true })
    },
    [navigate, search, pathname],
  )

  return {
    urlQueryParams: queryParams,
    originalQueryParams,
    setUrlQueryParams,
  }
}

export default useUrlQueryParams
